import { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Route as RouteIcon, Plus, Trash2, ChevronUp, ChevronDown, X, Loader2,
  PenTool, HelpCircle, Users2, BookOpen, ClipboardList,
} from 'lucide-react';

const TYPE_ICON = { module: PenTool, quiz: HelpCircle, assignment: ClipboardList, doc: BookOpen, custom: RouteIcon };

export default function LearningPathsPage() {
  const { get, post, put, del } = useApi();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('content.create');
  const canAssign = hasPermission('content.assign');

  const [paths, setPaths] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);

  // builder
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState([]);
  const [saving, setSaving] = useState(false);

  // picker
  const [pickerOpen, setPickerOpen] = useState(false);
  const [modules, setModules] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [pickerSearch, setPickerSearch] = useState('');

  // assign
  const [assignFor, setAssignFor] = useState(null);
  const [assignBatches, setAssignBatches] = useState(new Set());

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [p, a, b] = await Promise.all([
        get('/learning-paths'),
        get('/learning-paths/assignments/list'),
        get('/trainees/batches'),
      ]);
      setPaths(p.paths || []);
      setAssignments(a.assignments || []);
      setBatches(b.batches || []);
    } catch { toast.error('Failed to load learning paths'); }
    finally { setLoading(false); }
  }, [get]);
  useEffect(() => { loadAll(); }, [loadAll]);

  const openBuilder = (path = null) => {
    setEditing(path);
    setName(path?.name || '');
    setDescription(path?.description || '');
    setSteps(path ? path.steps.map(s => ({ ...s })) : []);
    setBuilderOpen(true);
  };

  const openPicker = async () => {
    setPickerOpen(true);
    if (modules.length === 0 && quizzes.length === 0) {
      try {
        const [m, q] = await Promise.all([get('/modules?limit=200'), get('/quizzes')]);
        setModules(m.modules || []);
        setQuizzes(Array.isArray(q) ? q : (q.quizzes || []));
      } catch { /* non-fatal */ }
    }
  };

  const addStep = (type, ref_id, title) => {
    setSteps(s => [...s, { id: `tmp_${Math.random().toString(36).slice(2, 8)}`, type, ref_id, title, required: true }]);
    toast.success('Step added');
  };
  const addCustom = () => setSteps(s => [...s, { id: `tmp_${Math.random().toString(36).slice(2, 8)}`, type: 'custom', ref_id: null, title: '', required: true }]);
  const updateStep = (i, patch) => setSteps(s => s.map((x, k) => k === i ? { ...x, ...patch } : x));
  const removeStep = (i) => setSteps(s => s.filter((_, k) => k !== i));
  const move = (i, dir) => setSteps(s => {
    const j = i + dir; if (j < 0 || j >= s.length) return s;
    const n = [...s]; [n[i], n[j]] = [n[j], n[i]]; return n;
  });

  const savePath = async () => {
    if (!name.trim()) { toast.error('Path name is required'); return; }
    const clean = steps.filter(s => s.title.trim()).map(s => ({
      type: s.type, ref_id: s.ref_id || null, title: s.title.trim(), required: !!s.required,
      ...(s.id && !s.id.startsWith('tmp_') ? { id: s.id } : {}),
    }));
    if (clean.length === 0) { toast.error('Add at least one step'); return; }
    setSaving(true);
    try {
      if (editing) await put(`/learning-paths/${editing.path_id}`, { name: name.trim(), description: description.trim() || null, steps: clean });
      else await post('/learning-paths', { name: name.trim(), description: description.trim() || null, steps: clean });
      toast.success(editing ? 'Path updated' : 'Path created');
      setBuilderOpen(false);
      await loadAll();
    } catch { toast.error('Save failed'); } finally { setSaving(false); }
  };

  const removePath = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This removes its batch assignments too.`)) return;
    try { await del(`/learning-paths/${p.path_id}`); toast.success('Deleted'); await loadAll(); }
    catch { toast.error('Delete failed'); }
  };

  const openAssign = (p) => { setAssignFor(p); setAssignBatches(new Set()); };
  const doAssign = async () => {
    if (assignBatches.size === 0) { toast.error('Select at least one batch'); return; }
    try {
      const res = await post(`/learning-paths/${assignFor.path_id}/assign`, { batch_ids: Array.from(assignBatches) });
      toast.success(`Assigned to ${res.assigned} batch(es)`);
      setAssignFor(null);
      await loadAll();
    } catch { toast.error('Assign failed'); }
  };

  const toggleStep = async (a, step) => {
    const next = step.status === 'done' ? 'pending' : 'done';
    try {
      await post(`/learning-paths/assignments/${a.assignment_id}/steps/${step.id}/toggle`, { status: next });
      await loadAll();
    } catch { toast.error('Update failed'); }
  };

  const unassign = async (a) => {
    if (!window.confirm(`Remove "${a.path_name}" from ${a.batch_name || 'this batch'}?`)) return;
    try { await del(`/learning-paths/assignments/${a.assignment_id}`); toast.success('Removed'); await loadAll(); }
    catch { toast.error('Remove failed'); }
  };

  const filteredModules = modules.filter(m => m.title?.toLowerCase().includes(pickerSearch.toLowerCase()));
  const filteredQuizzes = quizzes.filter(q => (q.title || q.name || '').toLowerCase().includes(pickerSearch.toLowerCase()));

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="learning-paths-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center"><RouteIcon className="w-5 h-5 text-amber-600" /></div>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight">Learning Paths</h1>
            <p className="text-sm text-muted-foreground">Build ordered curricula and assign them to batches.</p>
          </div>
        </div>
        {canEdit && <Button onClick={() => openBuilder()} data-testid="path-new-btn"><Plus className="w-4 h-4 mr-2" />New path</Button>}
      </div>

      <Tabs defaultValue="paths">
        <TabsList>
          <TabsTrigger value="paths" data-testid="paths-tab-templates"><RouteIcon className="w-4 h-4 mr-2" />Paths</TabsTrigger>
          <TabsTrigger value="assigned" data-testid="paths-tab-assigned"><Users2 className="w-4 h-4 mr-2" />Assigned ({assignments.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="paths" className="mt-4">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : paths.length === 0 ? (
            <Card className="p-10 text-center text-muted-foreground" data-testid="paths-empty">No learning paths yet.</Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {paths.map(p => (
                <Card key={p.path_id} className="p-4 space-y-3" data-testid={`path-card-${p.path_id}`}>
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0">
                      <div className="font-display font-semibold truncate">{p.name}</div>
                      {p.description && <div className="text-xs text-muted-foreground line-clamp-2">{p.description}</div>}
                    </div>
                    <Badge variant="secondary">{(p.steps || []).length} steps</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">{p.assignment_count || 0} batch assignment(s)</div>
                  <div className="flex flex-wrap gap-2">
                    {canAssign && <Button size="sm" variant="outline" onClick={() => openAssign(p)} data-testid={`path-assign-${p.path_id}`}><Users2 className="w-3.5 h-3.5 mr-1" />Assign</Button>}
                    {canEdit && <Button size="sm" variant="outline" onClick={() => openBuilder(p)} data-testid={`path-edit-${p.path_id}`}>Edit</Button>}
                    {canEdit && <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => removePath(p)} data-testid={`path-delete-${p.path_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="assigned" className="mt-4 space-y-4">
          {assignments.length === 0 ? (
            <Card className="p-10 text-center text-muted-foreground" data-testid="assigned-empty">No paths assigned to batches yet.</Card>
          ) : assignments.map(a => (
            <Card key={a.assignment_id} className="p-4 space-y-3" data-testid={`assignment-${a.assignment_id}`}>
              <div className="flex justify-between items-center gap-3">
                <div>
                  <div className="font-display font-semibold">{a.path_name}</div>
                  <div className="text-xs text-muted-foreground">Batch: {a.batch_name || '—'}</div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-sm font-semibold tabular-nums">{a.pct_complete}%</div>
                  {canAssign && <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => unassign(a)}><X className="w-4 h-4" /></Button>}
                </div>
              </div>
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div className="h-full bg-amber-500 transition-all" style={{ width: `${a.pct_complete}%` }} />
              </div>
              <div className="space-y-1.5">
                {a.step_progress.map(s => {
                  const Icon = TYPE_ICON[s.type] || RouteIcon;
                  return (
                    <label key={s.id} className="flex items-center gap-2 text-sm py-1 cursor-pointer" data-testid={`assignment-step-${a.assignment_id}-${s.id}`}>
                      <Checkbox checked={s.status === 'done'} disabled={!canAssign} onCheckedChange={() => canAssign && toggleStep(a, s)} />
                      <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className={s.status === 'done' ? 'line-through text-muted-foreground' : ''}>{s.title}</span>
                      {s.required && <span className="text-[10px] text-amber-600 uppercase">required</span>}
                    </label>
                  );
                })}
              </div>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {/* Builder dialog */}
      <Dialog open={builderOpen} onOpenChange={setBuilderOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="path-builder">
          <DialogHeader><DialogTitle>{editing ? 'Edit path' : 'New learning path'}</DialogTitle>
            <DialogDescription className="sr-only">Build an ordered learning path from modules, quizzes, and custom steps.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. New Hire Onboarding" data-testid="path-name-input" /></div>
            <div className="space-y-1.5"><Label>Description (optional)</Label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} /></div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Steps</Label>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={openPicker} data-testid="path-add-content-btn"><Plus className="w-3 h-3 mr-1" />Add content</Button>
                  <Button size="sm" variant="outline" onClick={addCustom} data-testid="path-add-custom-btn"><Plus className="w-3 h-3 mr-1" />Custom step</Button>
                </div>
              </div>
              {steps.length === 0 && <p className="text-sm text-muted-foreground">No steps yet. Add modules, quizzes, or a custom step.</p>}
              {steps.map((s, i) => {
                const Icon = TYPE_ICON[s.type] || RouteIcon;
                return (
                  <div key={i} className="flex items-center gap-2 rounded-lg border p-2" data-testid={`path-step-${i}`}>
                    <div className="flex flex-col">
                      <button onClick={() => move(i, -1)} disabled={i === 0} className="text-muted-foreground disabled:opacity-30"><ChevronUp className="w-3.5 h-3.5" /></button>
                      <button onClick={() => move(i, 1)} disabled={i === steps.length - 1} className="text-muted-foreground disabled:opacity-30"><ChevronDown className="w-3.5 h-3.5" /></button>
                    </div>
                    <Icon className="w-4 h-4 text-amber-600 shrink-0" />
                    {s.type === 'custom'
                      ? <Input className="flex-1 h-8" value={s.title} onChange={(e) => updateStep(i, { title: e.target.value })} placeholder="Custom step title" />
                      : <span className="flex-1 text-sm truncate">{s.title} <span className="text-xs text-muted-foreground">({s.type})</span></span>}
                    <div className="flex items-center gap-1 text-xs"><Switch checked={s.required} onCheckedChange={(v) => updateStep(i, { required: v })} />Req</div>
                    <Button size="icon" variant="ghost" onClick={() => removeStep(i)}><X className="w-4 h-4" /></Button>
                  </div>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBuilderOpen(false)}>Cancel</Button>
            <Button onClick={savePath} disabled={saving} data-testid="path-save-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? 'Save' : 'Create path')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Content picker */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="path-picker">
          <DialogHeader><DialogTitle>Add content step</DialogTitle><DialogDescription className="sr-only">Pick a module or quiz to add as a step.</DialogDescription></DialogHeader>
          <Input value={pickerSearch} onChange={(e) => setPickerSearch(e.target.value)} placeholder="Search modules & quizzes…" className="mb-2" />
          <div className="space-y-4">
            <div>
              <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">Modules</div>
              {filteredModules.length === 0 && <p className="text-sm text-muted-foreground">No modules.</p>}
              {filteredModules.map(m => (
                <div key={m.module_id} className="flex items-center justify-between py-1.5 border-b last:border-0">
                  <span className="text-sm truncate flex items-center gap-2"><PenTool className="w-3.5 h-3.5 text-muted-foreground" />{m.title}</span>
                  <Button size="sm" variant="outline" onClick={() => addStep('module', m.module_id, m.title)} data-testid={`picker-module-${m.module_id}`}>Add</Button>
                </div>
              ))}
            </div>
            <div>
              <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">Quizzes</div>
              {filteredQuizzes.length === 0 && <p className="text-sm text-muted-foreground">No quizzes.</p>}
              {filteredQuizzes.map(q => (
                <div key={q.quiz_id} className="flex items-center justify-between py-1.5 border-b last:border-0">
                  <span className="text-sm truncate flex items-center gap-2"><HelpCircle className="w-3.5 h-3.5 text-muted-foreground" />{q.title || q.name}</span>
                  <Button size="sm" variant="outline" onClick={() => addStep('quiz', q.quiz_id, q.title || q.name)} data-testid={`picker-quiz-${q.quiz_id}`}>Add</Button>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setPickerOpen(false)}>Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign dialog */}
      <Dialog open={!!assignFor} onOpenChange={(o) => !o && setAssignFor(null)}>
        <DialogContent className="max-w-md" data-testid="path-assign-dialog">
          <DialogHeader><DialogTitle>Assign "{assignFor?.name}" to batches</DialogTitle>
            <DialogDescription className="sr-only">Select which batches receive this learning path.</DialogDescription></DialogHeader>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {batches.length === 0 && <p className="text-sm text-muted-foreground">No batches available.</p>}
            {batches.map(b => (
              <label key={b.batch_id} className="flex items-center gap-2 text-sm py-1 cursor-pointer">
                <Checkbox checked={assignBatches.has(b.batch_id)} onCheckedChange={() => setAssignBatches(prev => {
                  const n = new Set(prev); n.has(b.batch_id) ? n.delete(b.batch_id) : n.add(b.batch_id); return n;
                })} data-testid={`assign-batch-${b.batch_id}`} />
                <span className="flex items-baseline gap-1.5 min-w-0">
                  {b.code && <span className="font-semibold whitespace-nowrap">{b.code}</span>}
                  {b.code && <span className="text-muted-foreground">|</span>}
                  <span className="truncate">{b.name}</span>
                  {b.department && <span className="text-muted-foreground">|</span>}
                  {b.department && <span className="text-xs text-muted-foreground truncate">{b.department}</span>}
                </span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignFor(null)}>Cancel</Button>
            <Button onClick={doAssign} data-testid="path-assign-confirm">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
